import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TablePagination,
  Button,
  Stack,
  TextField,
  InputAdornment,
  Grid,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import BlockIcon from '@mui/icons-material/Block';
import EditIcon from '@mui/icons-material/Edit';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import tesoreriaCajaService from '../../services/tesoreriaCajaService';
import EditarDescripcionEgresoDialog from './EditarDescripcionEgresoDialog';
import { formatCurrency, formatDate } from '../../utils/formatters';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ImportarMediosCobroDialog from './ImportarMediosCobroDialog';
import PDFViewer from '../shared/PDFViewer';
import { getFileNameFromContentDisposition } from '../../utils/contentDisposition';
import FiltroSucursalVentaSelect from '../shared/FiltroSucursalVentaSelect';
import SearchableSelect from '../SearchableSelect';
import { resolveNroSucFiltro, useSucursalesVentaFiltro } from '../../hooks/useSucursalesVentaFiltro';
import {
  loadTesoreriaFiltros,
  storedNumber,
  storedString,
  TESORERIA_FILTROS_KEYS,
  usePersistTesoreriaFiltros,
} from '../../utils/tesoreriaFiltrosStorage';
import { hasBrowserSession } from '../../utils/sessionAuthStore';

const ESTADO_MOVIMIENTO_OPTIONS = [
  { value: 'activos', label: 'Activos' },
  { value: 'anulados', label: 'Anulados' },
  { value: 'todos', label: 'Todos' },
] as const;

type FiltroEstadoMovimiento = (typeof ESTADO_MOVIMIENTO_OPTIONS)[number]['value'];

interface MovimientosVariosFiltrosStored {
  currentTab?: number;
  searchTerm?: string;
  fechaDesde?: string;
  fechaHasta?: string;
  filtroEstado?: FiltroEstadoMovimiento;
  filtroNroSuc?: number | null;
  rowsPerPage?: number;
}

const loadMovimientosVariosFiltros = (): Partial<MovimientosVariosFiltrosStored> => {
  const parsed = loadTesoreriaFiltros<MovimientosVariosFiltrosStored>(
    TESORERIA_FILTROS_KEYS.movimientosVarios
  );
  const estadosValidos = ESTADO_MOVIMIENTO_OPTIONS.map((o) => o.value) as string[];
  if (parsed.filtroEstado && !estadosValidos.includes(parsed.filtroEstado)) {
    parsed.filtroEstado = 'todos';
  }
  return parsed;
};

interface MovimientoVario {
  nroFormulario?: number;
  nroTipoFormulario?: string;
  nroSuc?: number;
  fechaEmision?: string;
  codigoCaja?: number;
  usuario?: string;
  nombre?: string;
  descripcion?: string;
  importeTotal?: number;
  letra?: string;
  nroComprobante?: number;
  tipoPago?: string;
  ingresoEgreso?: string;
  detalle?: string;
  asentado?: string;
  medioDeCobro?: string;
  medioCobro?: string;
  mcp?: string;
  medios?: Array<{
    ingresoEgreso?: string;
    [key: string]: any;
  }>;
  fecha?: string;
  comprobante?: string;
  tipo?: string;
  tipoComprobante?: string;
  cartera?: string;
  debe?: number;
  haber?: number;
  saldo?: number;
  [key: string]: any;
}

const MovimientosVarios: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const getFirstDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  };
  const getLastDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0);
  };

  const loadStored = (): Partial<Record<string, unknown>> => loadMovimientosVariosFiltros();

  const [currentTab, setCurrentTab] = useState(() => storedNumber(loadStored(), 'currentTab', 0));
  const [movimientos, setMovimientos] = useState<MovimientoVario[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState<string>('fechaEmision');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => storedNumber(loadStored(), 'rowsPerPage', 10));
  const [searchTerm, setSearchTerm] = useState(() => storedString(loadStored(), 'searchTerm', ''));
  const [fechaDesde, setFechaDesde] = useState<string>(() =>
    storedString(loadStored(), 'fechaDesde', getFirstDayOfMonth().toISOString().split('T')[0])
  );
  const [fechaHasta, setFechaHasta] = useState<string>(() =>
    storedString(loadStored(), 'fechaHasta', getLastDayOfMonth().toISOString().split('T')[0])
  );
  const [filtroEstado, setFiltroEstado] = useState<FiltroEstadoMovimiento>(
    () => loadMovimientosVariosFiltros().filtroEstado ?? 'todos'
  );
  const [totalRecords, setTotalRecords] = useState(0);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selectedMovimientoId, setSelectedMovimientoId] = useState<number | null>(null);
  const [pdfFileName, setPdfFileName] = useState<string | null>(null);
  const [anulandoId, setAnulandoId] = useState<number | null>(null);
  const [egresoDescripcion, setEgresoDescripcion] = useState<MovimientoVario | null>(null);
  const [guardandoDescripcion, setGuardandoDescripcion] = useState(false);
  const [openImportar, setOpenImportar] = useState(false);
  const { filtroNroSuc, setFiltroNroSuc, sucursales, loadingSucursales, filtroListo } = useSucursalesVentaFiltro({
    resetPreferidaOnNavigation: true,
  });

  /** Convierte fecha del formulario (YYYY-MM-DD) al formato del API (MM-DD-YYYY) */
  const toApiDate = (isoDate: string): string => {
    if (!isoDate) return '';
    const [y, m, d] = isoDate.split('-');
    return `${m}-${d}-${y}`;
  };

  const mapMovimiento = (item: any): MovimientoVario => ({
    ...item,
    asentado: item.asentado ?? item.Asentado,
  });

  const cargarMovimientos = async () => {
    setLoading(true);
    try {
      const tipoMovimiento = currentTab === 0 ? undefined : currentTab === 1 ? '5' : '7';
      const params: Record<string, string | number | undefined> = {
        FechaDesde: toApiDate(fechaDesde),
        FechaHasta: toApiDate(fechaHasta),
        PageNumber: page + 1,
        PageSize: Math.min(rowsPerPage, 50), // Backend: máximo 50
      };
      if (tipoMovimiento != null) {
        params.TipoMovimiento = tipoMovimiento;
      }
      const nroSucValue = resolveNroSucFiltro(filtroNroSuc);
      if (nroSucValue != null) {
        params.NroSuc = nroSucValue;
      }
      if (filtroEstado === 'anulados') {
        params.asentado = 'A';
      } else if (filtroEstado === 'activos') {
        params.asentado = 'activos';
      }
      if (searchTerm.trim()) {
        params.SearchTerm = searchTerm.trim();
      }

      const response = await api.get<any>('/api/TesoreriaCaja/movimientos-varios', { params });

      const data = response.data;
      let items: MovimientoVario[] = [];
      let totalCount = 0;

      if (Array.isArray(data)) {
        items = data.map(mapMovimiento);
        totalCount = data.length;
      } else if (data && Array.isArray(data.items)) {
        items = (data.items || []).map(mapMovimiento);
        totalCount = data.totalCount ?? items.length;
      } else if (data && data.data && Array.isArray(data.data)) {
        items = (data.data || []).map(mapMovimiento);
        totalCount = data.totalCount ?? items.length;
      }

      setMovimientos(items);
      setTotalRecords(totalCount);
    } catch (error: any) {
      console.error('Error al cargar movimientos:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Error al cargar los movimientos varios',
        { variant: 'error' }
      );
      setMovimientos([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  usePersistTesoreriaFiltros(
    TESORERIA_FILTROS_KEYS.movimientosVarios,
    { currentTab, searchTerm, fechaDesde, fechaHasta, filtroEstado, rowsPerPage },
    [currentTab, searchTerm, fechaDesde, fechaHasta, filtroEstado, rowsPerPage]
  );

  useEffect(() => {
    if (!filtroListo) return;
    cargarMovimientos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm, fechaDesde, fechaHasta, currentTab, filtroNroSuc, filtroEstado, filtroListo]);

  const handleNuevoMovimiento = () => {
    const tipo = currentTab === 1 ? 'ingreso' : currentTab === 2 ? 'egreso' : undefined;
    navigate(
      tipo
        ? `/tesoreria/movimientos-varios/nuevo?tipo=${tipo}`
        : '/tesoreria/movimientos-varios/nuevo'
    );
  };

  const movimientosConFiltros = movimientos;

  const movimientosFiltrados = movimientosConFiltros;

  const movimientosOrdenados = useMemo(() => {
    const sorted = [...movimientosFiltrados];
    sorted.sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      if (sortField === 'fechaEmision' || sortField === 'fecha') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = (bValue || '').toLowerCase();
      } else if (typeof aValue === 'number') {
        // Mantener como número
      } else {
        aValue = aValue || 0;
        bValue = bValue || 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [movimientosFiltrados, sortField, sortDirection]);

  const movimientosPaginados = movimientosOrdenados;

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setPage(0);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const formatComprobante = (m: MovimientoVario) => {
    const { letra, nroSuc, nroComprobante } = m;
    if (
      !letra ||
      nroSuc === undefined ||
      nroComprobante === undefined ||
      nroSuc === null ||
      nroComprobante === null
    ) {
      return '-';
    }
    const formatPart = (value: number | string | undefined | null, length: number) => {
      if (value === null || value === undefined) return ''.padStart(length, '0');
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) return numeric.toString().padStart(length, '0');
      return String(value).padStart(length, '0');
    };
    const sucursal = formatPart(nroSuc, 5);
    const comprobante = formatPart(nroComprobante, 8);
    const letraNormalizada = (letra || '').trim().toUpperCase();
    return letraNormalizada ? `${letraNormalizada}-${sucursal}-${comprobante}` : '-';
  };

  const esMovimientoAnulado = (movimiento: MovimientoVario) =>
    (movimiento.asentado ?? '').trim().toUpperCase() === 'A' ||
    (movimiento.nombre ?? '').trim().toUpperCase() === 'ANULADO';

  const puedeMostrarAnular = (movimiento: MovimientoVario) => !esMovimientoAnulado(movimiento);

  const puedeEditarDescripcion = (movimiento: MovimientoVario) =>
    String((movimiento.nroTipoFormulario || '').trim()) === '7' && !esMovimientoAnulado(movimiento);

  const handleAbrirDescripcion = (movimiento: MovimientoVario) => {
    if (movimiento.nroFormulario == null || !puedeEditarDescripcion(movimiento)) return;
    setEgresoDescripcion(movimiento);
  };

  const handleCerrarDescripcion = () => {
    if (guardandoDescripcion) return;
    setEgresoDescripcion(null);
  };

  const handleGuardarDescripcion = async (descripcion: string) => {
    if (egresoDescripcion?.nroFormulario == null) return;
    setGuardandoDescripcion(true);
    try {
      await tesoreriaCajaService.actualizarDescripcionEgresoVario(
        egresoDescripcion.nroFormulario,
        descripcion
      );
      enqueueSnackbar('Descripción actualizada', { variant: 'success' });
      setEgresoDescripcion(null);
      await cargarMovimientos();
    } catch (error: any) {
      const data = error?.response?.data;
      const message =
        (Array.isArray(data?.errors?.general) ? data.errors.general[0] : null) ??
        data?.message ??
        'Error al actualizar la descripción';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setGuardandoDescripcion(false);
    }
  };

  const formatMotivosAnulacion = (motivos?: string[] | null, fallback = 'No se puede anular este movimiento') => {
    const list = (motivos ?? []).map((m) => String(m).trim()).filter(Boolean);
    return list.length > 0 ? list.join('\n') : fallback;
  };

  const handleAnular = async (movimiento: MovimientoVario) => {
    if (movimiento.nroFormulario == null || !puedeMostrarAnular(movimiento)) return;

    try {
      const check = await tesoreriaCajaService.puedeAnularMovimientoVario(movimiento.nroFormulario);
      if (!check.puedeAnular) {
        enqueueSnackbar(formatMotivosAnulacion(check.motivos), {
          variant: 'warning',
          style: { whiteSpace: 'pre-line' },
          autoHideDuration: 8000,
        });
        return;
      }
    } catch (error: any) {
      const msg = formatMotivosAnulacion(
        error?.response?.data?.motivos,
        error?.response?.data?.message ?? 'No se pudo validar la anulación'
      );
      enqueueSnackbar(msg, {
        variant: 'error',
        style: { whiteSpace: 'pre-line' },
        autoHideDuration: 8000,
      });
      return;
    }

    if (
      !window.confirm(
        '¿Está seguro que desea anular este movimiento varios? Se revertirán los medios de cobro/pago asociados a caja.'
      )
    ) {
      return;
    }

    setAnulandoId(movimiento.nroFormulario);
    try {
      await tesoreriaCajaService.anularMovimientoVario(movimiento.nroFormulario);
      enqueueSnackbar('Movimiento anulado correctamente', { variant: 'success' });
      await cargarMovimientos();
    } catch (error: any) {
      const data = error?.response?.data;
      const message = formatMotivosAnulacion(
        data?.motivos,
        data?.message ??
          (Array.isArray(data?.errors?.general) ? data.errors.general[0] : null) ??
          'Error al anular el movimiento'
      );
      enqueueSnackbar(message, {
        variant: 'error',
        style: { whiteSpace: 'pre-line' },
        autoHideDuration: 8000,
      });
    } finally {
      setAnulandoId(null);
    }
  };

  const handleImprimirPDF = async (id: number) => {
    try {
      if (!hasBrowserSession()) {
        enqueueSnackbar('No hay token de autenticación', { variant: 'error' });
        return;
      }
      const response = await api.get(`/api/TesoreriaCaja/movimientos-varios/${id}/pdf`, {
        responseType: 'blob',
      });
      const cd = (response.headers as any)?.['content-disposition'] as string | undefined;
      const backendFileName = getFileNameFromContentDisposition(cd);
      setPdfFileName(backendFileName);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      setPdfUrl(url);
      setSelectedMovimientoId(id);
    } catch (error: any) {
      const status = error.response?.status;
      let message = 'Error al generar el PDF del movimiento';
      if (status === 404) {
        message = 'Movimiento no encontrado o no es un ingreso/egreso varios.';
      } else if (status === 500 && error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = text ? JSON.parse(text) : null;
          if (json?.detail) message = json.detail;
        } catch (_) {
          /* usar mensaje por defecto */
        }
      }
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const handleClosePDF = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    setPdfUrl(null);
    setSelectedMovimientoId(null);
    setPdfFileName(null);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: '#1976d2',
              }}
            >
              Movimientos Varios
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<UploadFileIcon />}
                onClick={() => setOpenImportar(true)}
                sx={{
                  borderRadius: 2,
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1.5, sm: 1 },
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                }}
              >
                Importar medios
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleNuevoMovimiento}
                sx={{
                  borderRadius: 2,
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1.5, sm: 1 },
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                  },
                }}
              >
                Nuevo Movimiento
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Buscar por número, comprobante, descripción o nombre"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#666' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Fecha Desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => {
                    setFechaDesde(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Fecha Hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => {
                    setFechaHasta(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <SearchableSelect
                  label="Estado"
                  options={[...ESTADO_MOVIMIENTO_OPTIONS]}
                  value={filtroEstado}
                  onChange={(value) => {
                    if (value) {
                      setFiltroEstado(value as FiltroEstadoMovimiento);
                      setPage(0);
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FiltroSucursalVentaSelect
                  value={filtroNroSuc}
                  onChange={(v) => {
                    setFiltroNroSuc(v);
                    setPage(0);
                  }}
                  loading={loadingSucursales}
                  sucursales={sucursales}
                />
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={currentTab} onChange={handleTabChange}>
              <Tab
                label={currentTab === 0 ? `TODOS ${totalRecords}` : 'TODOS'}
                sx={{
                  textTransform: 'none',
                  fontWeight: currentTab === 0 ? 600 : 400,
                  color: currentTab === 0 ? '#1976d2' : 'inherit',
                  fontSize: '0.875rem',
                }}
              />
              <Tab
                label={currentTab === 1 ? `INGRESOS ${totalRecords}` : 'INGRESOS'}
                icon={<ArrowUpwardIcon sx={{ color: '#4caf50', fontSize: '1.2rem' }} />}
                iconPosition="start"
                sx={{
                  textTransform: 'none',
                  fontWeight: currentTab === 1 ? 600 : 400,
                  color: currentTab === 1 ? '#1976d2' : 'inherit',
                  fontSize: '0.875rem',
                }}
              />
              <Tab
                label={currentTab === 2 ? `EGRESOS ${totalRecords}` : 'EGRESOS'}
                icon={<ArrowDownwardIcon sx={{ color: '#f44336', fontSize: '1.2rem' }} />}
                iconPosition="start"
                sx={{
                  textTransform: 'none',
                  fontWeight: currentTab === 2 ? 600 : 400,
                  color: currentTab === 2 ? '#1976d2' : 'inherit',
                  fontSize: '0.875rem',
                }}
              />
            </Tabs>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: 2,
                  boxShadow: 'none',
                  border: '1px solid #e0e0e0',
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Nro. Mov. Interno</TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, cursor: 'pointer', minWidth: 100 }}
                        onClick={() => handleSort('fechaEmision')}
                      >
                        Fecha {sortField === 'fechaEmision' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: 110 }}>Comprobante</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Motivo</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Importe
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Usuario</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>
                        Acciones
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {movimientosPaginados.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No hay movimientos para mostrar
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      movimientosPaginados.map((movimiento, index) => {
                        const nroTipoFormulario = String((movimiento.nroTipoFormulario || '').trim());
                        const esEgreso = nroTipoFormulario === '7';
                        const esIngreso = nroTipoFormulario === '5';
                        const anulado = esMovimientoAnulado(movimiento);

                        const nombre = movimiento.nombre ? movimiento.nombre.trim() : '-';
                        const fecha = movimiento.fechaEmision || '';

                        let tipo = '-';
                        if (nroTipoFormulario === '5') {
                          tipo = 'Ingreso';
                        } else if (nroTipoFormulario === '7') {
                          tipo = 'Egreso';
                        }

                        const importe = movimiento.importeTotal || 0;

                        const backgroundColor = anulado
                          ? '#f5f5f5'
                          : esIngreso
                            ? '#e8f5e9'
                            : esEgreso
                              ? '#ffebee'
                              : '#fff';

                        return (
                          <TableRow
                            key={movimiento.nroFormulario ?? index}
                            sx={{
                              backgroundColor,
                              opacity: anulado ? 0.75 : 1,
                              '&:hover': {
                                backgroundColor: anulado
                                  ? '#eeeeee'
                                  : esIngreso
                                    ? '#c8e6c9'
                                    : esEgreso
                                      ? '#ffcdd2'
                                      : '#f9f9f9',
                              },
                            }}
                          >
                            <TableCell sx={{ fontWeight: 500 }}>
                              {movimiento.nroFormulario ?? '-'}
                            </TableCell>
                            <TableCell>{fecha ? formatDate(fecha) : '-'}</TableCell>
                            <TableCell>{formatComprobante(movimiento)}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {esIngreso && !anulado && (
                                  <ArrowUpwardIcon sx={{ color: '#4caf50', fontSize: 16 }} />
                                )}
                                {esEgreso && !anulado && (
                                  <ArrowDownwardIcon sx={{ color: '#f44336', fontSize: 16 }} />
                                )}
                                {nombre}
                              </Box>
                            </TableCell>
                            <TableCell>{tipo}</TableCell>
                            <TableCell>
                              {anulado ? (
                                <Chip
                                  label="Anulado"
                                  size="small"
                                  sx={{ bgcolor: '#9e9e9e', color: '#fff', fontWeight: 500 }}
                                />
                              ) : (
                                <Chip
                                  label="Activo"
                                  size="small"
                                  sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 500 }}
                                />
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>
                              {importe > 0 ? formatCurrency(importe) : '-'}
                            </TableCell>
                            <TableCell>{movimiento.usuario ?? '-'}</TableCell>
                            <TableCell align="center">
                              {movimiento.nroFormulario != null && (
                                <Stack direction="row" spacing={0.5} justifyContent="center">
                                  <Tooltip title="Imprimir PDF">
                                    <IconButton
                                      size="small"
                                      onClick={() => void handleImprimirPDF(movimiento.nroFormulario!)}
                                      sx={{
                                        bgcolor: '#2196f3',
                                        color: '#fff',
                                        borderRadius: 1.5,
                                        width: 32,
                                        height: 32,
                                        '&:hover': { bgcolor: '#1976d2' },
                                      }}
                                    >
                                      <PrintIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  {puedeEditarDescripcion(movimiento) && (
                                    <Tooltip title="Editar descripción">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleAbrirDescripcion(movimiento)}
                                        sx={{
                                          bgcolor: '#ff9800',
                                          color: '#fff',
                                          borderRadius: 1.5,
                                          width: 32,
                                          height: 32,
                                          '&:hover': { bgcolor: '#f57c00' },
                                        }}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {puedeMostrarAnular(movimiento) && (
                                    <Tooltip title="Anular movimiento">
                                      <IconButton
                                        size="small"
                                        onClick={() => void handleAnular(movimiento)}
                                        disabled={anulandoId === movimiento.nroFormulario}
                                        sx={{
                                          bgcolor: '#f44336',
                                          color: '#fff',
                                          borderRadius: 1.5,
                                          width: 32,
                                          height: 32,
                                          '&:hover': { bgcolor: '#d32f2f' },
                                          '&:disabled': { bgcolor: '#ef9a9a', color: '#fff' },
                                        }}
                                      >
                                        {anulandoId === movimiento.nroFormulario ? (
                                          <CircularProgress size={16} sx={{ color: '#fff' }} />
                                        ) : (
                                          <BlockIcon fontSize="small" />
                                        )}
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Stack>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalRecords}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </>
          )}
        </CardContent>
      </Card>

      {pdfUrl && selectedMovimientoId != null && (
        <PDFViewer
          pdfUrl={pdfUrl}
          onClose={handleClosePDF}
          title={`Movimiento ${selectedMovimientoId}`}
          documentId={`${selectedMovimientoId}`}
          downloadFileName={pdfFileName ?? undefined}
        />
      )}

      <ImportarMediosCobroDialog
        open={openImportar}
        modo="registrar"
        onClose={() => setOpenImportar(false)}
        onRegistrado={() => {
          setOpenImportar(false);
          void cargarMovimientos();
        }}
      />

      <EditarDescripcionEgresoDialog
        open={egresoDescripcion != null}
        nroFormulario={egresoDescripcion?.nroFormulario ?? null}
        comprobanteLabel={egresoDescripcion ? formatComprobante(egresoDescripcion) : null}
        concepto={egresoDescripcion?.nombre}
        descripcionInicial={egresoDescripcion?.descripcion ?? ''}
        saving={guardandoDescripcion}
        onClose={handleCerrarDescripcion}
        onSave={(descripcion) => void handleGuardarDescripcion(descripcion)}
      />
    </Box>
  );
};

export default MovimientosVarios;
